import CLIENT_SETTINGS from './client_settings';

export const USAGE_EVENTS = [
  '$pageview', 'template_changed', 'file_upload_completed',
  'file_processing_requested', 'profile_generation_requested', 'stats_analysis_generated',
  'target_created', 'target_wizard_opened', 'target_wizard_generation_requested',
  'ground_saved', 'rpe_session_saved', 'calendar_period_changed', 'match_saved',
  'ai_analysis_requested', 'player_playback_started', 'export_requested',
  'export_download_requested', 'action_failed',
] as const;

export type UsageEvent = typeof USAGE_EVENTS[number];
export type UsageProperties = {
  type?: string;
  mode?: 'create' | 'update';
  format?: string;
  target?: string;
  count?: number;
  direction?: 'next' | 'previous' | 'select';
  action?: UsageEvent;
  reason?: 'request_failed' | 'rejected' | 'no_data' | 'upload_failed';
};

const types: Partial<Record<UsageEvent, readonly string[]>> = {
  profile_generation_requested: ['asp', 'mpp', 'comparison'],
  stats_analysis_generated: ['trend', 'summary', 'scatter', 'radar'],
  ai_analysis_requested: ['session', 'player_profile'],
};
const exportTargets = [
  'AthleteSession', 'AthleteSessionList', 'TeamSession', 'TeamSessionEvents',
  'TeamSessionRollingWindowPeaks', 'TeamSessionSplitted', 'IncomingFile', 'Track', 'Gpexe',
  'ASProfile', 'MPProfile', 'ASProfileList', 'ASProfileExtList', 'MPProfileList',
  'ASProfileComparison', 'MPProfileComparison', 'Trend', 'Summary', 'Radar', 'Scatter',
  'RPEList', 'TeamSessionRpe', 'AIAnalysis',
];

// Only route shapes leave the browser. Unknown paths are never transmitted verbatim.
const pages = [
  'welcome', 'clear', 'api', 'dashboard', 'calendar', 'athletes', 'tracks', 'sessions',
  'files', 'athlete_sessions', 'comparison', 'rpe', 'trend', 'summary', 'scatter', 'radar',
  'stats', 'as_profiles', 'mp_profiles', 'targets', 'templates', 'teams', 'users', 'grounds',
  'club', 'fullscreen-session-player', 'fullscreen-session-recorder', 'exports', 'changelog',
  'smartcases', 'my_stats',
];
const details = ['athletes', 'tracks', 'sessions', 'athlete_sessions', 'rpe', 'as_profiles',
  'mp_profiles', 'targets', 'templates', 'teams', 'users', 'grounds', 'smartcases'];

export function usagePage(pathname: string): string {
  const [page, detail, extra] = pathname.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (!page) return '/';
  if (!pages.includes(page)) return '/other';
  if (page === 'sessions' && detail === 'new' && !extra) return '/sessions/new';
  if (detail && details.includes(page)) {
    return `/${page}/:id${page === 'sessions' && extra === 'edit' ? '/edit' : ''}`;
  }
  return `/${page}`;
}

export function sanitizeUsageProperties(event: UsageEvent, properties: UsageProperties = {}) {
  const safe: Record<string, string | number> = {};
  if (types[event]?.includes(properties.type || '')) safe.type = properties.type as string;
  if (['ground_saved', 'match_saved', 'rpe_session_saved'].includes(event)
    && ['create', 'update'].includes(properties.mode || '')) safe.mode = properties.mode as string;
  if (event === 'calendar_period_changed' && ['next', 'previous', 'select'].includes(properties.direction || '')) {
    safe.direction = properties.direction as string;
  }
  if (['export_requested', 'export_download_requested'].includes(event)) {
    if (['pdf', 'csv', 'sensor_csv', 'xlsx', 'xml', 'txt', 'raw', 'zip'].includes(properties.format || '')) {
      safe.format = properties.format as string;
    }
    if (exportTargets.includes(properties.target || '')) safe.target = properties.target as string;
  }
  if (['file_upload_completed', 'file_processing_requested', 'profile_generation_requested',
    'rpe_session_saved', 'export_requested', 'export_download_requested'].includes(event)
    && Number.isSafeInteger(properties.count) && (properties.count as number) >= 0) {
    safe.count = properties.count as number;
  }
  if (event === 'action_failed') {
    if (USAGE_EVENTS.includes(properties.action as UsageEvent) && properties.action !== 'action_failed') {
      safe.action = properties.action as string;
    }
    if (['request_failed', 'rejected', 'no_data', 'upload_failed'].includes(properties.reason || '')) {
      safe.reason = properties.reason as string;
    }
  }
  return safe;
}

const STAGE_SERVERS = ['e04', 'e11', 'e15'];

export function analyticsEnvironment(serverId: string): string {
  const server = serverId.split('.')[0];
  if (STAGE_SERVERS.includes(server)) return 'stage';
  if (/^e\d+$/.test(server)) return 'stable';
  return server || 'unknown';
}

export function analyticsServerId(backendUrl: string, override?: string): string {
  if (override && /^[a-zA-Z0-9._-]+$/.test(override)) return override;
  try { return new URL(backendUrl).host; } catch { return 'unknown'; }
}

const config = CLIENT_SETTINGS.public.usageAnalytics;
const local = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
const mode = config.mode || (local ? 'console' : 'posthog');
const validToken = /^phc_[a-zA-Z0-9]+$/.test(config.token);
let sdk: Promise<typeof import('posthog-js').default | undefined> | undefined;
let eligible = false;
let owner: string | undefined;
let generation = 0;
let pageKey: string | undefined;

// The local account ID is used only to invalidate in-flight actions at account changes.
// It is never passed to PostHog, persisted by analytics, or included in console events.
export function setAnalyticsUser(user?: {
  id: string; isStaff: boolean; isSuperuser: boolean; excluded?: boolean;
}) {
  const nextEligible = !!user && user.isStaff === false && user.isSuperuser === false && !user.excluded;
  if (nextEligible !== eligible || user?.id !== owner) {
    generation += 1;
    pageKey = undefined;
  }
  eligible = nextEligible;
  owner = user?.id;
}

export function resetUsageAnalytics() {
  setAnalyticsUser();
}

function metadata(sourcePage: string) {
  const serverId = analyticsServerId(config.backendUrl, config.serverId);
  return {
    server_id: serverId,
    environment: local ? 'local' : analyticsEnvironment(serverId),
    frontend_version: CLIENT_SETTINGS.public.gpexeFeVersion || 'unknown',
    source_page: sourcePage,
    is_test: local || config.isTest,
  };
}

function loadPosthog() {
  sdk = sdk || import('posthog-js').then(({ default: posthog }) => {
    posthog.init(config.token, {
      api_host: config.apiHost,
      cookieless_mode: 'always',
      disable_product_tours: true,
      disable_web_experiments: true,
      person_profiles: 'never',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_performance: false,
      capture_exceptions: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      respect_dnt: true,
      // Defense in depth: drop SDK-generated events and strip every unapproved SDK property.
      before_send: (payload) => {
        if (!payload || !eligible || !USAGE_EVENTS.includes(payload.event as UsageEvent)) return null;
        const event = payload.event as UsageEvent;
        const properties = payload.properties || {};
        const safeProperties = {
          ...metadata(usagePage(properties.source_page || '/other')),
          ...sanitizeUsageProperties(event, properties),
          token: config.token,
          distinct_id: '$posthog_cookieless',
          $cookieless_mode: true,
          $host: window.location.host,
          $raw_user_agent: properties.$raw_user_agent,
          $timezone: properties.$timezone,
          $pathname: usagePage(properties.source_page || '/other'),
          $current_url: window.location.origin + usagePage(properties.source_page || '/other'),
          $process_person_profile: false,
        };
        return { ...payload, properties: safeProperties };
      },
    });
    return posthog;
  }).catch(() => undefined);
  return sdk;
}

function send(event: UsageEvent, properties: UsageProperties, sourcePage: string) {
  if (!USAGE_EVENTS.includes(event) || !eligible
    || mode === 'off' || (mode !== 'console' && mode !== 'posthog')) return;
  const safe = { ...metadata(sourcePage), ...sanitizeUsageProperties(event, properties) };
  try {
    if (mode === 'console') {
      console.info('[usage-analytics]', event, safe);
    } else if (validToken) {
      loadPosthog().then((posthog) => posthog?.capture(event, safe)).catch(() => undefined);
    }
  } catch {
    // Analytics must never interrupt a client workflow.
  }
}

export function recordUsage(event: UsageEvent, properties: UsageProperties = {}) {
  send(event, properties, usagePage(window.location.pathname));
}

export function recordUsagePageview(key: string) {
  if (!eligible || pageKey === key) return;
  pageKey = key;
  recordUsage('$pageview');
}

export function beginUsageAction(event: UsageEvent, properties: UsageProperties = {}, requested = false) {
  const startedEligible = eligible;
  const startedGeneration = generation;
  const sourcePage = usagePage(window.location.pathname);
  let settled = false;
  const capture = (name: UsageEvent, props: UsageProperties) => {
    if (startedEligible && startedGeneration === generation) send(name, props, sourcePage);
  };
  if (requested) capture(event, properties);
  return {
    complete: (completedProperties: UsageProperties = {}) => {
      if (settled) return;
      settled = true;
      if (!requested) capture(event, { ...properties, ...completedProperties });
    },
    fail: (reason: UsageProperties['reason'] = 'request_failed') => {
      if (settled) return;
      settled = true;
      capture('action_failed', { action: event, reason });
    },
  };
}
export type UsageAction = ReturnType<typeof beginUsageAction>;

// Apollo mutations with onError resolve with a single error object in errors, rather than rejecting.
function hasErrors(result: unknown): boolean {
  const response = result as { errors?: unknown; error?: unknown };
  return !!response?.error || (Array.isArray(response?.errors) ? response.errors.length > 0 : !!response?.errors);
}

// Observe explicit action promises, never Apollo links, fetch globally, polling, or subscriptions.
export function trackUsageAction<T>(
  event: UsageEvent,
  properties: UsageProperties,
  action: () => Promise<T>,
  requested = false,
): Promise<T> {
  const tracking = beginUsageAction(event, properties, requested);
  try {
    return action().then((result) => {
      const response = result as { data?: { res?: { success?: boolean } } };
      if (hasErrors(result)) tracking.fail();
      else if (response?.data?.res?.success === false) tracking.fail('rejected');
      else tracking.complete();
      return result;
    }, (error) => {
      tracking.fail();
      throw error;
    });
  } catch (error) {
    tracking.fail();
    throw error;
  }
}

export function settleUsageBatch(tracking: UsageAction, requests: Promise<unknown>[]) {
  return Promise.all(requests).then((results) => {
    const failed = results.some(hasErrors);
    if (failed) tracking.fail();
    else tracking.complete();
  }, (error) => {
    tracking.fail();
    throw error;
  });
}

export function usageExportFormat(filename?: string | null): string | undefined {
  const format = filename?.split('.').pop()?.toLowerCase();
  return ['pdf', 'csv', 'xlsx', 'xml', 'txt', 'raw', 'zip'].includes(format || '') ? format : undefined;
}
